import { PropertyValues, html, nothing, svg } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { Ref, createRef, ref } from 'lit/directives/ref.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { SchmancyElement } from '@schmancy/mixins/index'
import {
	CHART_W,
	ChartMixin,
	FILL,
	POINT_RADIUS,
	STROKE,
	TEXT,
	nearestChartPoint,
	renderChartTooltipCard,
	type ChartHoveredState,
	type ChartLayout,
	type ChartLayoutPoint,
} from './chart.mixin'
import type { ChartColorToken, ChartPadding, ChartPoint, ChartSelectEvent, ChartThreshold } from './types'

/**
 * Which points show their `label` caption under the plot.
 *
 * `fixed-3` and `all` are unmeasured — three hand-picked indices, or every
 * point regardless of whether they fit — so both are only safe on a series
 * whose length the call site controls. `auto` is the measured row every
 * `<schmancy-line-chart>` renders: one caption per point, thinned left→right
 * until they stop colliding, with the selected one always kept. Reach for it
 * whenever the point count comes from data.
 */
export type StepChartXLabels = 'none' | 'fixed-3' | 'all' | 'auto'

/** WCAG 2.5.8 minimum target, and `<schmancy-line-chart>`'s own floor — the
 *  step curve draws no per-point dot, so the hit target is the whole
 *  affordance and never shrinks to the marker's radius. */
const HIT_SIZE_PX = Math.max(POINT_RADIUS * 2, 24)

/**
 * Balance-against-thresholds chart: a true step curve (holds flat between
 * updates, jumps at each), tinted threshold bands + dashed rules + captions,
 * and a single marker — on the selected point, defaulting to the last —
 * colored by `.colorFor`.
 *
 * Every point carries a focusable hit target that emits `select`, so the chart
 * reads as data to a keyboard/AT user and drives a consumer's own detail
 * reveal. Transparent at rest: a consumer that never binds `.selected` renders
 * exactly what it did before this control existed.
 */
@customElement('schmancy-step-chart')
export class SchmancyStepChart extends ChartMixin(SchmancyElement) {
	@property({ type: Array }) thresholds: ChartThreshold[] = []
	@property({ attribute: false }) colorFor?: (p: ChartPoint) => ChartColorToken
	@property({ type: String }) xLabels: StepChartXLabels = 'none'

	/** Controlled — `-1` means "effective last". Same contract as
	 *  `<schmancy-line-chart>`: the consumer owns the value and re-binds it from
	 *  the `select` event. */
	@property({ type: Number }) selected = -1
	@property({ attribute: false }) ariaLabelFor?: (p: ChartPoint) => string

	/** Every point renders its own colored circle (via `.colorFor`), not just
	 *  the marked one — suppresses the marked point's own separate circle so
	 *  it isn't drawn twice. Named `dots`, not `points`: `points` collides with
	 *  `layout.points` in `renderUnstretchedMarks` below. */
	@property({ type: Boolean }) dots = false
	/** Hover tooltip card, reusing `<schmancy-area-chart>`'s pointer-distance
	 *  hit-test — a separate, easier-to-trigger hover affordance layered over
	 *  the per-point 24×24 hit targets below, which stay the click/select
	 *  mechanism (WCAG-sized) regardless of this prop. */
	@property({ type: Boolean }) tooltip = false

	override curve = 'step' as const

	override defaultPadding(): Required<ChartPadding> {
		return { top: 24, right: 20, bottom: this.xLabels !== 'none' ? 40 : 20, left: 20 }
	}

	// The caption row is tabular-nums, so its width is a pure function of its
	// longest string — one hidden probe, measured once per render pass rather
	// than per label. Rendered only under `auto`, the one mode that measures.
	@state() private captionW = 0
	private captionProbeRef: Ref<HTMLSpanElement> = createRef()

	protected override updated(changed: PropertyValues): void {
		super.updated(changed)
		// Post-render probe measurement, batched behind updateComplete — a
		// reactive write inside `updated()` schedules mid-cycle (Lit's
		// `change-in-update` warning on every mount); the batch schedules the
		// same follow-up cleanly and settles on the re-measure's equal value.
		this.updateComplete.then(() => {
			this.captionW = this.captionProbeRef.value?.getBoundingClientRect().width ?? 0
		})
	}

	/** The point the marker and its value callout sit on. `-1` — the default,
	 *  and every consumer that never binds `.selected` — resolves to the last
	 *  point, which is where both have always sat. An out-of-range index falls
	 *  back the same way rather than throwing on a stale binding. */
	private markedPoint(layout: ChartLayout): ChartLayoutPoint {
		const n = layout.points.length
		return layout.points[this.selected >= 0 && this.selected < n ? this.selected : n - 1]
	}

	override marksPoint(layout: ChartLayout, p: ChartLayoutPoint): boolean {
		return this.dots || p.index === this.markedPoint(layout).index
	}

	@state() private hovered: ChartHoveredState = { visible: false, index: -1 }

	override handlePointerMove = (e: PointerEvent): void => {
		if (!this.tooltip || !this.layout) return
		const el = this.plotRef.value
		if (!el) return
		const rect = el.getBoundingClientRect()
		if (rect.width === 0) return
		this.hovered = nearestChartPoint(this.layout, rect, e, this.hovered)
	}

	override handlePointerLeave = (): void => {
		this.hovered = { ...this.hovered, visible: false }
	}

	override renderBackgroundMarks(layout: ChartLayout) {
		const baseY = layout.padding.top + layout.chartHeight
		return svg`${repeat(
			this.thresholds,
			(t, i) => `${t.value}-${i}`,
			threshold => {
				const y = this.yFor(threshold.value)
				// The rule's dash runs along x, the one axis this viewBox stretches, so
				// without non-scaling-stroke — which strokes in screen space — a 3-unit
				// dash paints 3 * plotWidth / CHART_W px and the rhythm tracks the
				// container instead of holding still.
				return svg`
						${threshold.band === false ? nothing : svg`<rect
						x="0"
						y=${y}
						width=${CHART_W}
						height=${Math.max(0, baseY - y)}
						class=${FILL[threshold.color]}
						fill-opacity="0.1"
						></rect>`}
					<line
						x1="0"
						y1=${y}
						x2=${CHART_W}
						y2=${y}
						class=${STROKE[threshold.color]}
						stroke-width="1"
						stroke-opacity="0.5"
						stroke-dasharray="3 3"
						vector-effect="non-scaling-stroke"
					></line>
				`
			},
		)}`
	}

	override renderUnstretchedMarks(layout: ChartLayout) {
		// `dots` draws a circle at every point (including the marked one, in its
		// own `.colorFor` color) — the marked point's own separate circle below
		// is skipped so that index isn't drawn twice.
		if (this.dots) {
			return svg`${repeat(
				layout.points,
				p => p.index,
				p => svg`<circle cx="${this.xPercent(p)}%" cy=${p.y} r=${POINT_RADIUS} class=${FILL[this.colorFor?.(p.point) ?? 'primary']}></circle>`,
			)}`
		}
		const marked = this.markedPoint(layout)
		const token = this.colorFor?.(marked.point) ?? 'primary'
		return svg`<circle cx="${this.xPercent(marked)}%" cy=${marked.y} r=${POINT_RADIUS} class=${FILL[token]}></circle>`
	}

	override renderOverlays(layout: ChartLayout) {
		const marked = this.markedPoint(layout)
		const token = this.colorFor?.(marked.point) ?? 'primary'
		// The default (unset colorFor) token is 'primary' — rendered as plain
		// on-surface text rather than the primary accent, since the accent is
		// reserved for the warning/error/success states colorFor opts into.
		const valueTextClass = token !== 'primary' ? TEXT[token] : 'text-surface-on'
		const n = layout.points.length
		const xLabelIndices =
			this.xLabels === 'none'
				? new Set<number>()
				: this.xLabels === 'fixed-3'
					? new Set([0, Math.floor((n - 1) / 2), n - 1])
					: this.xLabels === 'auto'
						? this.keptLabelIndices(layout, this.captionW, marked.index)
						: new Set(layout.points.map(p => p.index))
		const longestCaption = layout.points.reduce((longest, p) => {
			const text = p.point.label ?? ''
			return text.length > longest.length ? text : longest
		}, '')

		return html`
			<div class="absolute inset-0 pointer-events-none">
				${repeat(
					this.thresholds.filter(t => t.label),
					(t, i) => `${t.value}-${i}`,
					t => html`
						<span
							class=${this.classMap({
								'absolute left-1 text-[10px] whitespace-nowrap': true,
								[TEXT[t.color]]: true,
							})}
							style=${this.styleMap({ top: `${this.yFor(t.value)}px`, transform: 'translateY(-100%)' })}
							>${t.label}</span
						>
					`,
				)}
				<span
					class=${this.classMap({
						'absolute -translate-y-[140%] text-[11px] font-semibold tabular-nums whitespace-nowrap': true,
						// Anchoring follows the marked point rather than assuming the
						// last one: right-anchored on the trailing edge so it never
						// clips, centered anywhere in between.
							'-translate-x-1/2 text-center': this.horizontalAlignment(marked) === 'center',
							'text-right': this.horizontalAlignment(marked) === 'right',
						[valueTextClass]: true,
					})}
					style=${this.styleMap({ ...this.horizontalAnchorStyle(marked), top: `${marked.y}px` })}
					>${this.formatValue(marked.point.value, marked.point)}</span
				>
				<!-- dots does not reposition this callout. item-detail's stock chart
				     (dots unset) must stay byte-identical, and a dots consumer's own
				     collision geometry is unasserted here — deliberately deferred as
				     its own step, not folded in silently. -->
			</div>
			<div class="absolute inset-0">
				${repeat(
					layout.points,
					p => p.index,
					p => html`
						<button
							type="button"
							class="absolute -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors hover:bg-surface-on/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-default"
							style=${this.styleMap({
								left: `${this.xPercent(p)}%`,
								top: `${p.y}px`,
								width: `${HIT_SIZE_PX}px`,
								height: `${HIT_SIZE_PX}px`,
							})}
							aria-label=${this.ariaLabelFor?.(p.point) ?? p.point.label ?? this.formatValue(p.point.value, p.point)}
							aria-pressed=${p.index === marked.index}
							@click=${() => {
								const event: ChartSelectEvent = new CustomEvent('select', {
									detail: { index: p.index, point: p.point, data: p.point.data },
									bubbles: true,
									composed: true,
								})
								this.dispatchEvent(event)
							}}
						></button>
					`,
				)}
			</div>
			${when(
				this.xLabels === 'auto',
				() => html`
					<span
						${ref(this.captionProbeRef)}
						class="invisible absolute pointer-events-none whitespace-nowrap text-[10px] tabular-nums"
						>${longestCaption}</span
					>
				`,
			)}
			${when(
				this.xLabels !== 'none',
				() => html`
					<div class="absolute inset-x-0 bottom-0.5 h-4 pointer-events-none">
						${repeat(
							layout.points.filter(p => xLabelIndices.has(p.index)),
							p => p.index,
							p => html`
								<span
									class=${this.classMap({
										'absolute text-[10px] text-surface-on-variant opacity-60 whitespace-nowrap tabular-nums': true,
										'-translate-x-1/2': this.horizontalAlignment(p) === 'center',
										'text-right': this.horizontalAlignment(p) === 'right',
									})}
									style=${this.styleMap(this.horizontalAnchorStyle(p))}
									>${p.point.label ?? ''}</span
								>
							`,
						)}
					</div>
				`,
			)}
			${when(
				this.tooltip,
				() => renderChartTooltipCard(layout, this.hovered, (v, p) => this.formatValue(v, p)),
				() => nothing,
			)}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-step-chart': SchmancyStepChart
	}
}
